import { ClipboardList, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import type { ServiceRecord } from '@/types/service-record'

interface ServiceRecordTableProps {
  records: ServiceRecord[]
  showCustomer?: boolean
  showActions?: boolean
  onEdit?: (r: ServiceRecord) => void
  onDelete?: (r: ServiceRecord) => void
}

export function ServiceRecordTable({ records, showCustomer = true, showActions = true, onEdit, onDelete }: ServiceRecordTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {showCustomer && <TableHead>客户</TableHead>}
          <TableHead>服务类型</TableHead>
          <TableHead>主题</TableHead>
          <TableHead>内容</TableHead>
          <TableHead>图片</TableHead>
          <TableHead>录入人</TableHead>
          <TableHead>录入时间</TableHead>
          <TableHead>下次回访</TableHead>
          {showActions && <TableHead className="text-right">操作</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map(r => (
          <TableRow key={r.id}>
            {showCustomer && <TableCell className="font-medium">{r.customer_name || r.customer_id}</TableCell>}
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {r.types.map(t => (
                  <Badge key={t.id} variant="secondary">{t.label}</Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="max-w-48 truncate" title={r.subject}>{r.subject}</TableCell>
            <TableCell className="max-w-64 truncate" title={r.content}>{r.content || '-'}</TableCell>
            <TableCell>
              {r.images.length === 0 ? '-' : (
                <div className="flex gap-1">
                  {r.images.map(img => (
                    <a key={img.id} href={img.path} target="_blank" rel="noreferrer">
                      <img src={img.path} alt="服务图片" className="h-10 w-10 rounded border object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap tabular-nums">{r.user_name || r.user_id}</TableCell>
            <TableCell className="whitespace-nowrap tabular-nums">{(r.record_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
            <TableCell className="whitespace-nowrap tabular-nums">{r.next_service_at ? r.next_service_at.slice(0, 10) : '-'}</TableCell>
            {showActions && (
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <PermissionGuard permission="service_record:update">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit?.(r)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard permission="service_record:delete">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDelete?.(r)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
        {records.length === 0 && (
          <TableRow>
            <TableCell colSpan={showCustomer ? 9 : 8} className="py-12 text-center">
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <ClipboardList className="size-8 opacity-50" />
                <p className="text-sm">暂无服务记录</p>
                <p className="text-xs">调整筛选条件，或从客户列表录入一次服务</p>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
